<template>
  <div
    class="editor-view-container overflow-hidden"
  >
    <EditorSkeletonLoader
      v-if="pageLoading || componentLoading"
      :side-by-side-view="Boolean(sideBySideView)"
    />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="sideBySideView"
    >
      <template #full-width-header>
        <EditorSubMenu />
      </template>

      <template #left>
        <SideBar
          :body-messages="bodyMessages"
          :is-data-loaded="!pageLoading"
          :has-valid-score="hasValidScore"
          :ranking="ranking"
          :sidebar-messages="sidebarMessages"
          :scales="scales"
          :type_key="type_key"
          :saved-status="savedStatus"
          :focus="focus"
          :nudges="nudges"
          :is-revision-saving="isRevisionSaving"
          :inclusion-meter-disabled="authData.inclusionMeterDisabled"
          @one-click-add="handleOneClickAdd"
          @track-sidebar-event="trackEvent($event)"
        />
      </template>

      <template #content>
        <EditorsContainer
          :show-red-border-around-title="showRedBorderAroundTitle"
          :company="company"
          :is-template="isTemplate"
          :is-linked="isLinked"
          :location="location"
          :title="title"
          :is-data-loaded="!pageLoading"
          :job="document"
          :document-type-label="documentTypeLabel"
          :is-editable="isEditable"
          :editors-init-state="editorsInitState"
          :set-description-action="setDescriptionAction"
          :set-location-action="setLocationAction"
          :set-company-action="setCompanyAction"
          :set-flexible-id-action="setFlexibleIdAction"
          :set-title-action="setTitleAction"
          :editor-data-source="editorDataSource"
          :set-handle-cleanup-before-rescore-action="setHandleCleanupBeforeRescoreAction"
          :update-and-save="updateAndSave"
          :editor-initialized-action="editorInitializedAction"
          :project-integration-type="projectIntegrationType"
          :job-integration-type-key="integrationTypeKey"
          :job-id="$route.params.id"
          :tags="tags"
          :focus="focus"
          :section-nudges="sectionNudges"
          :block-nudges="blockNudges"
          :flexible-id="flexibleId"
          :side-by-side-view="sideBySideView"
        />
      </template>

      <template #content-footer>
        <EditorToolbarContainer
          class="pt-1 pb-1"
          :show-comments="showComments"
        />
      </template>

      <template #right>
        <RightPanel />
      </template>

      <template #side-revision>
        <SideBySideContainer
          :job-id="jobId"
          :is-linked="isLinked"
        />
      </template>
    </EditorLayout>
  </div>
</template>

<script>
import { useCommenting } from '@/stores/commenting/commenting';

/* eslint-disable camelcase,no-inner-declarations,guard-for-in,no-restricted-syntax */
import _, { isEmpty } from 'lodash-es';
import EditorSubMenu from '@/components/editor/EditorSubMenu.vue';
import EditorsContainer from '@/components/editor/EditorsContainer.vue';
import { recruitInitEditor } from '@/api/recruit';
import { vueGet, vueRescore } from '@/api/vue';
import { mixpanelIdentify } from '@/helpers/thirdParty/mixpanel';
import SideBar from '@/components/document/sideBar/SideBar.vue';
import EditorSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/EditorSkeletonLoader.vue';
import SideBySideContainer from '@/components/editor/SideBySideContainer.vue';
import GraphemeSplitter from 'grapheme-splitter';
import { mapState, mapActions } from 'pinia';
import { getQueryParamsFromURL } from '@/helpers/routing';
import { randomHash } from '@/helpers/hash';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAlerts } from '@/stores/app/alerts';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useDatapeopleAnywhereEditor } from '@/stores/document/datapeopleAnywhereEditor';
import { useDocumentEditor } from '@/stores/document/editor';
import { useAppBase } from '@/stores/app/base';
import { useEditor } from '@/stores/editor/editor';
import { useFirstDraft } from '@/stores/document/firstDraft';
import * as Sentry from '@sentry/vue';
import { getReadableTimezone } from '@/helpers/timeZone';
import { getEnvironment } from '@/helpers/environment';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import EditorToolbarContainer from '@/components/document/editorToolbar/EditorToolbarContainer.vue';
import RightPanel from '@/components/document/rightPanel/RightPanel.vue';

export default {
  name: 'EditorView',
  components: {
    EditorSubMenu,
    EditorsContainer,
    SideBar,
    EditorSkeletonLoader,
    SideBySideContainer,
    EditorLayout,
    EditorToolbarContainer,
    RightPanel,
  },
  beforeRouteLeave(_to, _from, next) {
    if (!this.isRevisionSaving) {
      return next();
    }

    // eslint-disable-next-line no-alert
    const answer = window.confirm('Save in progress. Are you sure you want to leave?'
      + '\nClick OK to leave');

    return next(answer);
  },
  data() {
    return {
      jobId: this.$route.params.id,
      revisionId: this.$route.query.revision || null,
      showRedBorderAroundTitle: false,
      title: '',
      company: '',
      location: '',
      flexibleId: '',
      description: [[]],
      description_delta: '',
      editorDataSource: '',
      editorsInitState: {
        description: false, title: false, location: false, company: false, flexibleId: false,
      },
      sidebarMessages: {
        'red-solid': [],
        'red-underline': [],
        'orange-solid': [],
        'orange-underline': [],
        'yellow-solid': [],
        'yellow-underline': [],
        'purple-solid': [],
        'purple-underline': [],
        'blue-solid': [],
        'blue-underline': [],
        'grey-solid': [],
        'grey-underline': [],
        'green-solid': [],
      },
      nudges: [],
      sectionNudges: [],
      blockNudges: [],
      tags: [],
      highlights: {},
      scales: {
        genderbias: { value: 0 },
        engagement: { value: 0 },
        clarity: { value: 0 },
        completeness: { value: 0 },
        mechanics: { value: 0 },
      },
      ranking: {},
      savedStatus: 2,
      lastRequestHash: '',
      hasChangedSinceLastRescore: false,
      showTemplateBlock: false,
      type_key: null,
      freeze: false,
      firstOpened: false,
      activeSelection: false,
      activeHighlight: false,
      isLinked: null,
      tapwageId: null,
      tapwageIntegrationTypeKey: null,
      isSharePage: null,
      templates: [],
      hasShareUsers: null,
      handleCleanupBeforeRescore() {},
      jobStatus: '',
      jobIntegrationType: '',
      projectIntegrationType: '',
      isWorkdaySyncAllowed: false,
      flashColor: null,
      shareModalUsers: {},
      companyName: '',
      showInvite: false,
      isEditable: true,
      isFlagship: false,
      focus: {
        blockId: null,
        sectionId: null,
        sectionType: null,
        blockType: null,
      },
      initialRescoreDone: false,
      datapeopleAnywhereSkipNextRescore: false,
    };
  },
  head() {
    const title = this.$route.meta.title(this) || 'Editor';
    const parentTitle = this.$route.meta.parentTitle(this) || '';

    if (parentTitle) {
      return { title: `${title} - ${parentTitle}` };
    }

    return { title };
  },
  computed: {
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useAppLoading, ['pageLoading', 'componentLoading']),
    ...mapState(useDatapeopleAnywhereEditor, ['isDatapeopleAnywhereEditor']),
    ...mapState(useDatapeopleAnywhereEditor, {
      datapeopleAnywhereJobId: 'jobId',
      datapeopleAnywhereRevisionId: 'revisionId',
      datapeopleAnywhereAtsJobId: 'atsJobId',
      datapeopleAnywhereAtsPostId: 'atsPostId',
    }),
    ...mapState(useDocumentEditor, {
      documentEditorFields: (state) => state.fields,
    }),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),
    ...mapState(useEditor, [
      'fields',
      'document',
      'documentType',
      'moveAnimation',
      'saveRequestStatus',
      'saveDebounceHandler',
      'tooltipVisible',
      'bodyMessages',
      'isExternal',
      'isEvergreen',
      'score',
      'isTemplate',
      'documentTypeLabel',
      'blockList',
      'isRevisionSaving',
      'sideBySideView',
      'integrationTypeKey',
    ]),
    hasTitleAndDocumentType() {
      return this.title && this.documentType;
    },
    hasValidScore() {
      return true;
    },
    view() {
      return this.$route.query.view || 'standard';
    },
    extensionSidebarMessages() {
      const sidebarMessages = [];
      const buckets = {
        'red-solid': [],
        'red-underline': [],
        'orange-solid': [],
        'orange-underline': [],
        'yellow-solid': [],
        'yellow-underline': [],
        'purple-solid': [],
        'purple-underline': [],
        'blue-solid': [],
        'blue-underline': [],
        'grey-solid': [],
        'grey-underline': [],
        'green-solid': [],
      };

      Object.keys(this.bodyMessages).forEach((id) => {
        const message = this.bodyMessages[id];

        if (message.action !== 'sidebar') {
          return;
        }

        if (message.is_nudge) {
          return;
        }

        buckets[message.severity_class]?.push({
          score_impact: message.score,
          severity_class: message.severity_class,
          tooltip_long: message.tooltip_long,
          tooltip_short: message.tooltip_short,
        });
      });

      Object.keys(buckets).forEach((severity_class) => {
        let bucket = buckets[severity_class];
        bucket.sort((a, b) => {
          const as = a.score_impact;
          const bs = b.score_impact;
          return as - bs;
        });
        bucket = bucket.slice(0, 3);
        bucket.forEach((message) => {
          sidebarMessages.push(message);
        });
      });

      return sidebarMessages;
    },
    showComments() {
      return useCommenting().showComments;
    },
  },
  watch: {
    browserBackOrForwardNavigation(newValue) {
      /**
       * Verifies the correct path in case this is triggered
       * on a back button click to a different page
       */
      const path = window.location.pathname;
      const searchParams = getQueryParamsFromURL();

      if (newValue && path.includes('/app/view/')) {
        this.$router.replace({
          name: 'editor-view-reload',
          params: { id: path.substring(path.lastIndexOf('/') + 1) },
          query: searchParams,
        });
      }
    },
    /**
     * Handles setting the state for all the editor loading animations.
     * Loads job title, company and location first, then loads each block individually.
     * The last stage to load is the side bar
     * @param {boolean} newValue
     */
    pageLoading: function loadingWatchHandler(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.addToLoadingAnimation('header');
          if (this.blockList) {
            this.blockList.forEach((block, i) => {
              setTimeout(() => {
                this.addToLoadingAnimation(`block-${block.id}`);
              }, (1000 / this.blockList.length) * (i + 1));
            });
          }
          setTimeout(() => {
            this.addToLoadingAnimation('sidebar');
            this.setLoadingAnimation({ showAnimation: false });
          }, 1000);
        }, 500);
      }
    },
    activeHighlight() {
      this.checkIsBusy();
    },
    activeSelection() {
      this.checkIsBusy();
    },
    editorsInitState: {
      handler(editorInitState) {
        if (
          (this.documentType === 'job'
            && !this.pageLoading
            && ((!this.descriptionHasSomeBlocks() || editorInitState.description)
              && editorInitState.title
              && (!this.flexibleId || editorInitState.flexibleId)
            )
          )
          || (
            this.documentType === 'job_template'
              && !this.pageLoading
              && (!this.descriptionHasSomeBlocks() || editorInitState.description)
          )
        ) {
          if (!this.initialRescoreDone) {
            this.initialRescoreDone = true;

            // new job created with First Draft.
            // the initial rescore is not needed.
            // new content generation will trigger rescore.
            if (this.$route.query.writeWithFirstDraft && this.authData?.aiEnabled) {
              // remove query param
              this.$router.replace({
                query: { ...this.$route.query, writeWithFirstDraft: undefined },
              }).catch({});
              // generate new content
              useFirstDraft().generateAndApply();
              return;
            }

            if (this.isDatapeopleAnywhereEditor) {
              window.addEventListener('message', this.handleDatapeopleAnywherePostMessage);
              window.parent.postMessage({ name: 'datapeopleDocumentReady' }, '*');
            } else {
              this.saveImmediately();
            }
          }
        }
      },
      deep: true,
    },
    moveAnimation(moves) {
      const pageScroll = moves.page;

      if (!pageScroll) {
        return;
      }

      window.scrollTo({
        top: window.scrollY + pageScroll,
        behavior: 'smooth',
      });
    },
    tooltipVisible(visible) {
      if (visible) {
        this.pauseDebounce();
      } else {
        this.resumeDebounce();
      }
    },
    isRevisionSaving(status) {
      const fn = status ? 'addEventListener' : 'removeEventListener';
      window[fn]('beforeunload', this.onBeforeUnload, { capture: true });

      window.parent.postMessage({
        name: status ? 'datapeopleDisableCloseButton' : 'datapeopleEnableCloseButton',
      }, '*');
    },
    // Updates the recentViews localStorage after we get the title and documentType
    hasTitleAndDocumentType(newValue) {
      if (newValue) {
        const recentRoutes = JSON.parse(localStorage.getItem('recentViews')) || [];
        const recentRouteIndex = recentRoutes.findIndex((route) => route.name === 'editor-view' && route.params.id === this.jobId);
        const { title, documentTypeLabel } = this;
        const { title: titleFunc, parentTitle: parentTitleFunc } = this.$route?.meta || {};

        if (typeof recentRouteIndex === 'number' && recentRoutes?.[recentRouteIndex] && titleFunc && parentTitleFunc) {
          recentRoutes[recentRouteIndex].meta.title = titleFunc({ title });
          recentRoutes[recentRouteIndex].meta.parentTitle = parentTitleFunc({ documentTypeLabel });

          localStorage.setItem('recentViews', JSON.stringify(recentRoutes));
        }
      }
    },
  },
  mounted() {
    this.setLoading({ pageLoading: true });
    this.setLoadingAnimation({ showAnimation: true, skeleton: true });
    useCommenting().setShowComments(true);
  },
  async created() {
    this.setInitFields({ jobId: this.jobId });
    this.debouncedTrackEvent = _.debounce(this.trackTitleUpdate, 2000);
    /* reset fields to default to avoid rescore error */
    this.setDescriptionJson({ fields: [] });
    this.updateFromData({});

    const start = Date.now();
    useTracking().editorPage({ id: this.jobId });

    const [recruitInitEditorResponse, vueGetResponse] = await this.getInitialData();
    // if an error thrown and handled
    if (!recruitInitEditorResponse || !vueGetResponse) {
      return;
    }

    if (recruitInitEditorResponse.success && recruitInitEditorResponse.body) {
      // these fields are used by shared job. we don't request auth-data in this case,
      // so we take the values from init response
      const {
        aiEnabled, noEditorEnabled, inclusionMeterDisabled,
      } = recruitInitEditorResponse.body;
      useAppAuthData().setAuthData({
        ...this.authData,
        aiEnabled,
        noEditorEnabled,
        inclusionMeterDisabled,
      });

      this.setInitFields(recruitInitEditorResponse.body);

      // add all backend flash messages to app alerts
      const flashMessages = recruitInitEditorResponse.body.flashMessages || {};
      Object.keys(flashMessages || {}).forEach((type) => {
        flashMessages[type].forEach((message) => {
          this.addAlert({ type, message });
        });
      });
      delete recruitInitEditorResponse.body.flashMessages;

      // add special non-editable alert
      if (!recruitInitEditorResponse.body.isEditable) {
        if (recruitInitEditorResponse.body.integrationTypeKey === 1) {
          // for non-editable published/unpublished job in Tapwage projects
          this.addAlert({
            type: 'info',
            message: '__integrationTapwageNonEditable__',
            thisPageOnly: true,
            params: {
              jobId: this.jobId,
              hideNewDraftLink: window?.sessionStorage?.shareKey,
              jobStatus: recruitInitEditorResponse.body.jobStatus?.toLowerCase() || '',
            },
          });
        } else {
          const messageKey = recruitInitEditorResponse?.body?.isFlagship
            ? '__nonEditableFlagship__'
            : '__nonEditable__';

          this.addAlert({
            type: 'info',
            message: messageKey,
            thisPageOnly: true,
            params: { jobId: this.jobId, hideNewDraftLink: window?.sessionStorage?.shareKey },
          });
        }
      }

      if (
        recruitInitEditorResponse.body.documentType === 'job_template'
        && !recruitInitEditorResponse.body.isHidden
      ) {
        this.addAlert({
          id: 'publishedTemplate',
          type: 'info',
          message: 'This template is published and cannot be edited. To make changes, toggle "Template" to "Hidden," make updates and toggle it back.',
          thisPageOnly: true,
        });
      }

      if (recruitInitEditorResponse.body.isSharePage) {
        const {
          mixpanel, user,
        } = recruitInitEditorResponse.body.thirdPartyConfigs || {};

        mixpanelIdentify({ mixpanel });

        const timezone = getReadableTimezone();
        const isDatapeopleAnywhere = this.view === 'chrome';

        Sentry.setUser({
          id: user?.userId,
          email: user?.email,
          username: user?.displayName,
          ip_address: '{{auto}}',
        });

        if (timezone) Sentry.setTag('timezone', timezone);
        if (isDatapeopleAnywhere) Sentry.setTag('isDatapeopleAnywhere', 'true');

        if (window.Beacon && getEnvironment() === 'production' && !isEmpty(user)) {
          window.Beacon('identify', {
            email: user?.email,
            name: user?.displayName,
            'project-id': user?.projectId,
          });
        }

        this.addSuccessSnackbar({
          text: 'You are viewing a shared version of this job.',
        });
      }

      // these fields have been moved to editor vuex module
      [
        'documentType', 'specificityKey', 'atsRequisitionId',
        'isApplicationReportingEnabled', 'atsTotalApplicants',
        'integrationTypeKey', 'isExternal', 'isEvergreen',
        'aiEnabled',
      ].forEach((field) => {
        delete recruitInitEditorResponse.body[field];
      });

      Object.keys(recruitInitEditorResponse.body).forEach((key) => {
        this[key] = recruitInitEditorResponse.body[key];
      });
    }

    if (
      this.documentType === 'job_template'
      && !this.authData?.canEditTemplates
      && !this.isSharePage
    ) {
      this.$router.replace({ name: 'root' });
    }

    this.updateFromData(vueGetResponse, 'get');
    this.setLoading({ pageLoading: false });

    this.datapeopleAnywhereUpdateIds({
      jobId: null, revisionId: null, atsJobId: null, atsPostId: null,
    });

    useTracking().editorLoaded({
      job: this.document, props: { duration: Date.now() - start },
    });
  },
  errorCaptured(err) {
    if (err?.message === 'Highlight text not found in the field content at all') {
      this.addAlert({ type: 'error', message: err.message, onlyOnceId: 'highlights' });
      return false;
    }
    if (err?.message?.startsWith('Cannot find a field for highlight')) {
      this.addAlert({ type: 'error', message: err.message, onlyOnceId: 'highlights2' });
      return false;
    }
    return true;
  },
  methods: {
    ...mapActions(useEditor, [
      'addToLoadingAnimation',
      'cancelSaveDebouncer',
      'setBlockHtml',
      'setBlockInitialized',
      'setDescriptionJson',
      'setDocumentFields',
      'setInitFields',
      'setLoadingAnimation',
      'setSaveRequestStatus',
      'setSaveDebouncer',
      'updateFieldSections',
    ]),
    ...mapActions(useAppLoading, ['setLoading']),
    ...mapActions(useAppAlerts, ['addAlert']),
    ...mapActions(useAppSnackbar, ['addMultilineErrorSnackbar', 'addSuccessSnackbar', 'addErrorSnackbar']),
    ...mapActions(useDatapeopleAnywhereEditor, ['findOrCreateDatapeoleAnywhereJob']),
    ...mapActions(useDatapeopleAnywhereEditor, {
      datapeopleAnywhereUpdateIds: 'updateIds',
      datapeopleAnywhereLoadInitialData: 'loadInitialData',
    }),
    ...mapActions(useDocumentEditor, [
      'oneClickAddSuggestion',
      'updateDocumentFromAts',
      'updateTitle',
      'updateCompany',
      'updateLocation',
      'updateFlexibleId',
    ]),

    trackEvent({ action }) {
      useTracking().editorAction({ action });
    },
    async getInitialData() {
      /* Begin backend calls to initialize Editor view */
      let recruitInitEditorResponse;
      let vueGetResponse;

      if (this.isDatapeopleAnywhereEditor) {
        return this.getInitialDataForDatapeopleAnywhere();
      }

      try {
        [recruitInitEditorResponse, vueGetResponse] = await Promise.all([
          recruitInitEditor({ id: this.$route.params.id }),
          vueGet({ id: this.$route.params.id, revision: this.revisionId }),
        ]);
      } catch (error) {
        this.setLoading({ pageLoading: false });
        if ([
          'You do not have permission to edit this job',
          'You do not have permission to revert this job',
          'You do not have permission to download this job',
        ].includes(error.message)) {
          this.addErrorSnackbar({ text: error.message });
          return [];
        }
        if (error.response?.code === 'job_not_found') {
          this.addErrorSnackbar({ text: 'Document not found' });
          return [];
        }
        if (error.response?.code === 'access_denied') {
          this.addErrorSnackbar({ text: 'You do not have permission to view this job' });
          return [];
        }

        if (error.response?.code === 'document_not_found') {
          this.addErrorSnackbar({ text: 'Document not found' });
          return [];
        }

        if (error.response?.code === 'access_denied') {
          this.addErrorSnackbar({ text: 'You do not have permission to view this job' });
          return [];
        }

        this.addAlert({ type: 'error', message: `${error.message}` });
        throw error;
      }

      return [recruitInitEditorResponse, vueGetResponse];
    },

    async getInitialDataForDatapeopleAnywhere() {
      try {
        return this.datapeopleAnywhereLoadInitialData(this.$route.query.atsId);
      } catch (error) {
        this.setLoading({ pageLoading: false });

        if (error.response?.code === 'ats_not_found') {
          this.addErrorSnackbar({ text: 'ATS not found' });
          return [];
        }

        this.addAlert({ type: 'error', message: `${error.message}` });
        throw error;
      }
    },

    handleDatapeopleAnywherePostMessage(event) {
      try {
        const { data } = event;
        const {
          name,
          job_id: jobId,
          revision_id: revisionId,
          ats_job_id: atsJobId,
          ats_post_id: atsPostId,
          ...payload
        } = data;

        if (name === 'datapeopleAtsContent') {
          this.updateDocumentFromAts(payload);
          this.saveImmediately();

          // we save atsJobId and atsPostId so consecutive rescores will create new revisions
          this.datapeopleAnywhereUpdateIds({
            jobId, revisionId, atsJobId, atsPostId,
          });
          this.datapeopleAnywhereSkipNextRescore = true;
          if (!atsJobId && !atsPostId) {
            // workday provides no id
            this.datapeopleAnywhereUpdateIds({ atsJobId: String(Math.random()) });
          }
        }

        if (name === 'datapeopleSendJobContent') {
          window.parent.postMessage({
            name: 'datapeopleJobContent',
            ...this.getJobContentForAts(),
            sidebarMessages: this.extensionSidebarMessages,
            score: this.score,
            jobId: this.datapeopleAnywhereJobId,
            revisionId: this.datapeopleAnywhereRevisionId,
            atsJobId: this.datapeopleAnywhereAtsJobId,
            atsPostId: this.datapeopleAnywhereAtsPostId,
          }, '*');
        }
      } catch (error) {
        this.addAlert({ type: 'error', message: `${error.message}` });
        throw error;
      }
    },

    handleOneClickAdd(suggestion) {
      // Update Pinia
      this.oneClickAddSuggestion(suggestion);

      const lastUnlockedFieldIndex = this.documentEditorFields
        .findLastIndex(({
          isLocked,
          sections,
          isHeaderOrFooter,
        }) => !isLocked && !isHeaderOrFooter && sections.some(({ lockType }) => lockType !== 'content'));

      const lastUnlockedField = this.documentEditorFields?.[lastUnlockedFieldIndex];

      if (!lastUnlockedField) return;

      // Update Vuex using Pinia's updated fields
      this.updateFieldSections({
        fieldIndex: lastUnlockedFieldIndex,
        sections: lastUnlockedField.sections,
      });

      // Save the changes
      this.updateAndSave();
    },
    setDescriptionAction(html, field, section, block, source) {
      this.setBlockHtml({
        field, section, block, html,
      });
      this.description = this.getHtmlFromBlocks();
      this.editorDataSource = source;
    },
    setLocationAction(text, source) {
      this.location = text;
      this.updateLocation(text);
      this.editorDataSource = source;
    },
    setCompanyAction(text, source) {
      this.company = text;
      this.updateCompany(text);
      this.editorDataSource = source;
    },
    trackTitleUpdate() {
      useTracking().editorAction({ action: 'Updated Title' });
    },
    setTitleAction(text, source) {
      this.debouncedTrackEvent();
      this.title = text;
      this.updateTitle(text);
      this.editorDataSource = source;
    },
    setFlexibleIdAction(text, source) {
      this.flexibleId = text;
      this.updateFlexibleId(text);
      this.editorDataSource = source;
    },
    initializeEditorDataAction(
      description,
      description_delta,
      description_json,
      title,
      company,
      location,
      flexible_id,
      source,
    ) {
      this.editorDataSource = source;
      this.description = description;
      this.description_delta = JSON.parse(description_delta);
      this.setDescriptionJson(description_json);
      this.title = title;
      this.location = location;
      this.company = company;
      this.flexibleId = flexible_id;
    },
    appendHTMLtoDescriptionAction(htmlString) {
      this.description += htmlString;
    },
    setHandleCleanupBeforeRescoreAction(func) {
      this.handleCleanupBeforeRescore = func;
    },
    editorInitializedAction(editorName, fieldIndex, sectionIndex, blockIndex) {
      if (editorName === 'description') {
        this.setBlockInitialized({
          field: fieldIndex, section: sectionIndex, block: blockIndex, initialized: true,
        });
        this.editorsInitState[editorName] = this.allBlocksInitialized();
      } else {
        this.editorsInitState[editorName] = true;
      }
    },
    pauseDebounce() {
      if (this.saveDebounceHandler) {
        this.cancelSaveDebouncer();
      }
    },
    resumeDebounce() {
      if (this.savedStatus !== 2) {
        this.save();
      }
    },
    updateFromData(data, type) {
      this.setDocumentFields({ documentData: data, isRescore: type === 'rescore' });

      // these fields have been moved to editor vuex module
      [
        'specificityData',
        'score',
        'bodyMessages',
      ].forEach((field) => {
        // eslint-disable-next-line no-param-reassign
        delete data[field];
      });

      this.descriptionApplyHighlights = true;
      this.titleApplyHighlights = true;
      this.locationApplyHighlights = true;

      for (const key in data) {
        if (key === 'hash') {
          // eslint-disable-next-line no-continue
          continue;
        }
        if (key === 'id') {
          this.revisionId = data.id;
          this.setInitFields({ revision_id: data.id });
        }
        if (key === 'description'
          || key === 'description_delta'
          || key === 'location'
          || key === 'title'
          || key === 'company'
          || key === 'flexible_id'
          || key === 'description_json') {
          // eslint-disable-next-line no-continue
          continue;
        } else {
          this[key] = data[key];
        }
      }

      if (type === 'get') {
        this.initializeEditorDataAction(
          data.description,
          data.description_delta,
          data.description_json,
          data.title,
          data.company,
          data.location,
          data.flexible_id,
          'init',
        );
      }

      const containingDiv = document.createElement('div');
      let split = [];
      const splitter = new GraphemeSplitter();
      this.fields.forEach((field) => {
        field.sections.forEach((section) => {
          section.blocks.forEach((block) => {
            containingDiv.innerHTML = block.html;
            const format = containingDiv.children[0].tagName;
            if (format === 'UL') {
              /*
                HTML element 'children' are an array-like object, but not an actual JS array.
                Here, we must convert it into one with spread.
              */
              const divChildren = [...containingDiv.firstElementChild.children];
              divChildren.forEach((child) => {
                split = [...split, ...splitter.splitGraphemes(child.textContent), '\n'];
              });
            } else {
              split = [...split, ...splitter.splitGraphemes(containingDiv.textContent), '\n'];
            }
          });
        });
      });
      let previouslyAppliedCompensation = 0;
      for (let i = 0; i < split.length; i++) {
        const graphemeLength = split[i].length;
        if (graphemeLength > 1) {
          // update highlight lengths
          for (const highlightKey in this.bodyMessages) {
            const highlight = this.bodyMessages[highlightKey];
            if (highlight.highlights.description.length > 0) {
              // is it inside of this highlight?
              // eslint-disable-next-line vue/max-len
              if (highlight.highlights.description[0].offset - previouslyAppliedCompensation <= i && (highlight.highlights.description[0].offset - previouslyAppliedCompensation + highlight.highlights.description[0].length) > i) {
                highlight.highlights.description[0].length += (graphemeLength - 1);
              }
            }
          }

          // update highlight offsets
          for (const highlightKey in this.bodyMessages) {
            const highlight = this.bodyMessages[highlightKey];
            if (highlight.highlights.description.length > 0) {
              if (highlight.highlights.description[0].offset - previouslyAppliedCompensation > i) {
                highlight.highlights.description[0].offset += (graphemeLength - 1);
              }
            }
          }
          previouslyAppliedCompensation += graphemeLength - 1;
        }
      }

      // derive block limits
      let from = 0;
      let to;
      this.fields.forEach((field) => {
        field.sections.forEach((section) => {
          section.blocks.forEach((block) => {
            const div = document.createElement('div');
            div.innerHTML = block.html;
            const format = div.children[0].tagName;
            let { length } = div.textContent;
            if (format === 'UL') {
              // Count li"s
              length += div.children[0].children.length - 1;
            }
            to = from + length;
            // eslint-disable-next-line no-param-reassign
            block.from = from;
            // eslint-disable-next-line no-param-reassign
            block.to = to;
            from = to + 1;
          });
        });
      });

      // match highlights to blocks
      // clear highlights
      this.fields.forEach((field) => {
        field.sections.forEach((section) => {
          section.blocks.forEach((block) => {
            // TODO: Test no longer needing to use `set`
            block.highlights = []; // eslint-disable-line no-param-reassign
          });
        });
      });

      // match highlights to blocks
      this.fields.forEach((field) => {
        field.sections.forEach((section) => {
          section.blocks.forEach((block) => {
            for (const highlightKey in this.bodyMessages) {
              const highlight = this.bodyMessages[highlightKey];
              if (highlight.highlights.description.length > 0) {
                if ((highlight.highlights.description[0].offset >= block.from)
                  && (highlight.highlights.description[0].offset <= block.to)
                ) {
                  // eslint-disable-next-line operator-assignment
                  highlight.highlights.description[0].offset = highlight.highlights
                    .description[0].offset - block.from;
                  highlight.field = field.id;
                  highlight.section = section.id;
                  highlight.block = block.id;
                  block.highlights.push(highlight);
                }
              }
            }
          });
        });
      });

      // bucket messages for sidebar
      // severity order
      const bucketed = {
        'red-solid': [],
        'red-underline': [],
        'orange-solid': [],
        'orange-underline': [],
        'yellow-solid': [],
        'yellow-underline': [],
        'purple-solid': [],
        'purple-underline': [],
        'blue-solid': [],
        'blue-underline': [],
        'grey-solid': [],
        'grey-underline': [],
        'green-solid': [],
      };

      let titleHighlights = [];
      let locationHighlights = [];
      let descriptionHighlights = [];
      let i; let
        k;

      if (this.hasValidScore) {
        for (i in this.bodyMessages) {
          const thisMessage = this.bodyMessages[i];
          if (thisMessage.action === 'sidebar') {
            // action sidebar
            bucketed[thisMessage.severity_class]?.push(thisMessage);
          } else {
            // not sidebar, potential highlight
            for (k in thisMessage.highlights.title) {
              titleHighlights.push({
                hash: i,
                action: thisMessage.action,
                severity: thisMessage.severity,
                length: thisMessage.highlights.title[k].length,
                offset: thisMessage.highlights.title[k].offset,
              });
            }
            for (k in thisMessage.highlights.location_raw) {
              locationHighlights.push({
                hash: i,
                action: thisMessage.action,
                severity: thisMessage.severity,
                length: thisMessage.highlights.location_raw[k].length,
                offset: thisMessage.highlights.location_raw[k].offset,
              });
            }
            for (k in thisMessage.highlights.description) {
              descriptionHighlights.push({
                hash: i,
                action: thisMessage.action,
                severity: thisMessage.severity,
                length: thisMessage.highlights.description[k].length,
                offset: thisMessage.highlights.description[k].offset,
              });
            }
          }
        }

        for (const bucket in bucketed) {
          bucketed[bucket] = _.orderBy(bucketed[bucket], ['score'], ['asc']);
        }

        /**
         *
         * @param sidebarMessages
         */
        function getNudges(sidebarMessages) {
          let output = [];
          for (const key in sidebarMessages) {
            output = output.concat(sidebarMessages[key]
              .filter((sidebarMessage) => sidebarMessage.is_nudge === true));
          }
          return output;
        }

        /**
         *
         * @param sidebarMessages
         */
        function sidebarMessagesWithoutNudges(sidebarMessages) {
          const output = {};
          for (const key in sidebarMessages) {
            output[key] = sidebarMessages[key]
              .filter((sidebarMessage) => sidebarMessage.is_nudge === false);
          }
          return output;
        }
        const nudges = getNudges(bucketed);
        const bodyNudges = nudges.filter((nudge) => !nudge.is_important);
        const sectionNudges = bodyNudges.filter((nudge) => nudge.focus_blocks.length === 0);
        const blockNudges = bodyNudges.filter((nudge) => nudge.focus_blocks.length > 0);
        this.nudges = nudges;
        this.sectionNudges = sectionNudges;
        this.blockNudges = blockNudges;
        this.sidebarMessages = sidebarMessagesWithoutNudges(bucketed);

        // remove highlights that are completely enclosed
        titleHighlights = this.removeEnclosedHighlights(titleHighlights);
        locationHighlights = this.removeEnclosedHighlights(locationHighlights);
        descriptionHighlights = this.removeEnclosedHighlights(descriptionHighlights);
      }
    },
    getCharCount(s) {
      const span = document.createElement('span');
      span.innerHTML = s;
      const content = span.textContent || span.innerText;
      return content.length;
    },
    updateAndSave() {
      this.savedStatus = 0;
      this.lastRequestHash = '';
      this.hasChangedSinceLastRescore = true;
      this.save();
    },
    saveImmediately() {
      if (!this.fields || this.fields.length === 0) {
        return;
      }

      const start = Date.now();

      this.setSaveDebouncer(null);
      this.savedStatus = 1;

      const that = this;
      if (this.handleCleanupBeforeRescore) {
        // is declared by description Editor component in hacky way
        this.handleCleanupBeforeRescore();
      }

      if (this.isDatapeopleAnywhereEditor && this.datapeopleAnywhereSkipNextRescore) {
        this.datapeopleAnywhereSkipNextRescore = false;
        return;
      }

      // remove highlights and save cursor position if applicables
      const data = {
        detached: this.isDatapeopleAnywhereEditor && !this.datapeopleAnywhereJobId,
        isExtension: this.view === 'chrome',
        id: this.isDatapeopleAnywhereEditor
          ? this.datapeopleAnywhereRevisionId
          : that.revisionId,
        title: that.title,
        location: that.location,
        company: that.company,
        flexibleId: that.flexibleId,
        description: that.getHtmlFromBlocks(),
        descriptionDelta: '[]',
        descriptionJson: {
          fields: that.fields.map((field) => ({
            id: field.id,
            type: field.type,
            formatBlacklist: field.formatBlacklist,
            label: field.label,
            sections: field.sections.map((section) => ({
              id: section.id,
              type: section.type,
              lockType: section.lockType,
              formatBlacklist: section.formatBlacklist,
              blocks: section.blocks.map((block) => ({
                id: block.id,
                type: block.type,
                lockType: block.lockType,
                formatBlacklist: block.formatBlacklist,
                html: block.html,
              })),
            })),
          })),
        },
        hash: randomHash(),
      };
      // immediately freeze
      this.freeze = true;

      // create random hash
      that.lastRequestHash = data.hash;
      that.hasChangedSinceLastRescore = false;
      data.key = window.sessionStorage.shareKey;
      this.setSaveRequestStatus('saving');
      // send to server
      let rescorePromise = null;

      if (
        this.isDatapeopleAnywhereEditor
        && !this.datapeopleAnywhereJobId
        && (this.datapeopleAnywhereAtsPostId || this.datapeopleAnywhereAtsJobId)
      ) {
        rescorePromise = this.findOrCreateDatapeoleAnywhereJob().then(({ revisionId }) => {
          // yes, data.id = revisionId, we send revision id as "id" when we rescore
          data.id = revisionId;
          data.detached = false;
          return vueRescore(data);
        });
      } else {
        rescorePromise = vueRescore(data);
      }

      rescorePromise.then((responseData) => {
        if (this.isDatapeopleAnywhereEditor) {
          // eslint-disable-next-line no-param-reassign
          responseData.description_json.fields = responseData.description_json.fields
            .filter(({ type }) => !type.endsWith('_header') && !type.endsWith('_footer'));
          if (this.datapeopleAnywhereJobId && responseData.id) {
            this.datapeopleAnywhereUpdateIds({ revisionId: responseData.id });
          }
        }
        /**
         * We should not update the document if a tooltip is visible,
         * otherwise, the tooltip buttons will break.
         * Instead, let's schedule another save for later
         * and hope there will be no more visible tooltips.
         */
        if (this.tooltipVisible) {
          this.save();
          return;
        }

        if (that.lastRequestHash === responseData.hash && !that.hasChangedSinceLastRescore) {
          that.updateFromData(responseData, 'rescore');
          that.savedStatus = 2;
          that.freeze = false;

          useTracking().editorRescore({
            job: { ...this.document, score: this.score, revisionId: responseData.id },
            props: { duration: Date.now() - start },
          });
        }

        this.setSaveRequestStatus('completed');
      }).catch((err) => {
        that.freeze = false;
        this.setSaveRequestStatus('failed');

        if (err.response?.code === 'not_allowed') {
          this.addErrorSnackbar({ text: 'You are not allowed to perform this action.' });
          return;
        }

        this.addMultilineErrorSnackbar({
          text: `An error occurred while analyzing this job.
            Our support team will contact you once the issue has been resolved.
            ${err.message}
          `,
        });
        throw err;
      });
    },
    save() {
      /**
       * If a tooltip is visible, the saveDebounceHandler is already canceled by pauseDebounce().
       * A new save is not needed at the moment. resumeDebounce() will schedule a new save when
       * the tooltip is not visible anymore.
       */
      if (this.tooltipVisible) {
        return;
      }

      if (this.saveDebounceHandler) {
        this.cancelSaveDebouncer();
      }
      this.setSaveRequestStatus('debouncing');

      const debounceHandle = _.debounce(function debouncedSave() {
        if (this.$route.query?.duplicate) {
          this.$router.replace({
            query: { ...this.$route.query, duplicate: undefined },
          }).catch({});
          return;
        }

        this.saveImmediately();
      }, 3000);

      this.setSaveDebouncer(debounceHandle);
      this.saveDebounceHandler();
    },
    checkIsBusy() {
      if (this.activeSelection || this.activeHighlight) {
        this.pauseDebounce();
      } else {
        this.resumeDebounce();
      }
    },
    getHtmlFromBlocks() {
      let descriptionHtml = '';
      this.fields.forEach((field) => {
        field.sections.forEach((section) => {
          section.blocks.forEach((block) => {
            descriptionHtml += block.html;
          });
        });
      });
      return descriptionHtml;
    },

    removeEnclosedHighlights(hl) {
      const sortfunc = (a, b) => b.length - a.length;
      hl.sort(sortfunc);

      const filtered = []; const inserts = []; const noninserts = []; let i; let
        k;

      // must always include insert actions
      for (i = 0; i < hl.length; i++) {
        if (hl[i].action === 'insert_before_hl' || hl[i].action === 'insert_after_hl') {
          inserts.push(hl[i]);
        } else {
          noninserts.push(hl[i]);
        }
      }

      for (i = 0; i < noninserts.length; i++) {
        let enclosed = false;
        const start = noninserts[i].offset;
        const end = start + noninserts[i].length;
        for (k = 0; k < filtered.length; k++) {
          const filt_start = filtered[k].offset;
          const filt_end = filt_start + filtered[k].length;
          if ((start >= filt_start) && (end <= filt_end)) {
            enclosed = true;
            break;
          }
        }
        if (!enclosed) {
          filtered.push(noninserts[i]);
        }
      }

      for (i = 0; i < inserts.length; i++) {
        filtered.push(inserts[i]);
      }

      return filtered;
    },
    descriptionHasSomeBlocks() {
      return this.fields
        .some((field) => field.sections.some((section) => section.blocks.some(() => true)));
    },
    allBlocksInitialized() {
      return this.fields
        .every((field) => field.sections.every((section) => section.blocks
          .every((block) => block.initialized === true)));
    },
    onBeforeUnload(event) {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign,no-return-assign
      return event.returnValue = 'Save in progress. Are you sure you want to leave?';
    },
    getJobContentForAts() {
      return {
        title: this.title,
        location: this.location,
        company: this.company,
        flexibleId: this.flexibleId || '',
        fields: this.fields.reduce((res, { type, sections }) => {
          let fieldHtml = '';

          sections.forEach(({ blocks }) => {
            blocks.forEach(({ html }) => {
              fieldHtml += html;
            });
          });

          return { ...res, [type]: fieldHtml };
        }, {}),
      };
    },
  },
};
</script>

<style>
  .row {
    flex-wrap: nowrap!important;
  }
</style>

<style scoped lang="less">
.container-below-menus {
  max-width: 1800px;
}
.container-below-menus-row {
  padding: 0;
  margin: 0;
  display: flex;
  width: 100%;
}
</style>
